import { useCallback, useEffect, useState } from 'react';
import { Card, Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Size } from '@src/Components/designConstants';
import { awaitJSON, FlashtalkingLambdaFetch, SheetsLambdaFetch } from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';

interface FlashtalkingCreativeLibraryRow {
  advertiser_name: string;
  company: string;
  creative_library_id: number;
  library_name: string;
}

const uniqueSorted = (values: string[]): DropdownOption[] =>
  [...new Set(values)]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ value, label: value }));

export const FlashtalkingCreativeLibraryAdder = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [advertiserName, setAdvertiserName] = useState<string>();
  const [company, setCompany] = useState<string>();
  const [companyOptions, setCompanyOptions] = useState<string[]>();
  const [creativeLibraryID, setCreativeLibraryID] = useState<number>();
  const [libraries, setLibraries] = useState<FlashtalkingCreativeLibraryRow[]>();
  const [selectedLibrary, setSelectedLibrary] = useState<FlashtalkingCreativeLibraryRow>();
  const [name, setName] = useState<string>();

  const { setError } = utils;
  const clMap: Record<string, FlashtalkingCreativeLibraryRow> = Object.fromEntries(
    (libraries ?? []).map(library => [`${library.creative_library_id}`, library])
  );

  useEffect(() => {
    (async () => {
      if (!libraries) {
        const librariesResp = await FlashtalkingLambdaFetch('/getFlashtalkingCreativeLibraries', {
          method: 'GET',
          params: {}
        });
        setLibraries(await awaitJSON<FlashtalkingCreativeLibraryRow[]>(librariesResp));
      }
    })();
  }, [libraries]);

  useEffect(() => {
    if (!companyOptions) {
      (async () => {
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const companies = await awaitJSON<{ ids: string[] }>(res);
          setCompanyOptions(companies.ids);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [companyOptions, setError]);

  const addFlashtalkingCreativeLibrary = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await FlashtalkingLambdaFetch('/addFlashtalkingCreativeLibrary', {
        method: 'POST',
        body: {
          advertiser_name: advertiserName,
          company,
          creative_library_id: creativeLibraryID,
          library_name: name
        }
      });
      setLibraries(undefined);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [advertiserName, company, creativeLibraryID, name, setError]);

  const libraryOptions: DropdownOption[] = (libraries ?? []).map(library => ({
    value: `${library.creative_library_id}`,
    label: `${library.library_name} (${library.creative_library_id})`
  }));

  const companyDropdownOptions: DropdownOption[] = (companyOptions ?? []).map(value => ({
    value,
    label: value
  }));

  const advertiserOptions = uniqueSorted((libraries ?? []).map(library => library.advertiser_name));

  return (
    <Card className='ad-ops-card' data-testid='ft-creative-library'>
      <div className='ad-ops-card-title'>
        Add/Update Flashtalking Creative Libraries
        <InfoTooltip>Use this tool to update the company name of creative libraries</InfoTooltip>
      </div>
      <Dropdown
        size={Size.LG}
        label='Existing library'
        placeholder='Select a creative library to edit…'

        menuPortalTarget={document.body}
        searchable
        clearable
        value={selectedLibrary ? `${selectedLibrary.creative_library_id}` : null}
        options={libraryOptions}
        onChange={id => {
          if (!id) {
            return;
          }
          setSelectedLibrary(clMap[id]);
          setAdvertiserName(clMap[id].advertiser_name);
          setCompany(clMap[id].company);
          setCreativeLibraryID(clMap[id].creative_library_id);
          setName(clMap[id].library_name);
        }}
      />
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Creative Library ID</InputGroup.Text>
        <Form.Control
          value={creativeLibraryID ?? ''}
          onChange={e => {
            if (e.target.value === '') {
              setCreativeLibraryID(undefined);
            }
            if (!Number.isNaN(parseInt(e.target.value, 10))) {
              setCreativeLibraryID(parseInt(e.target.value, 10));
            }
          }}
        />
      </InputGroup>
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Library Name</InputGroup.Text>
        <Form.Control value={name ?? ''} onChange={e => setName(e.target.value)} />
      </InputGroup>
      <div className='ad-ops-elem' data-testid='ft-creative-library-advertiser'>
        <Dropdown
          size={Size.LG}
          label='Advertiser Name'
          placeholder='Select or type an advertiser…'

          menuPortalTarget={document.body}
          searchable
          creatable
          clearable
          value={advertiserName || null}
          options={advertiserOptions}
          onChange={value => setAdvertiserName(value ?? undefined)}
          reactSelectProps={{ formatCreateLabel: input => `Use "${input}"` }}
        />
      </div>
      <div className='ad-ops-elem' data-testid='ft-creative-library-company'>
        <Dropdown
          size={Size.LG}
          label='Company'
          placeholder='Select or type a company…'

          menuPortalTarget={document.body}
          searchable
          creatable
          clearable
          value={company || null}
          options={companyDropdownOptions}
          onChange={value => setCompany(value ?? undefined)}
          reactSelectProps={{ formatCreateLabel: input => `Use "${input}"` }}
        />
      </div>
      <Button
        size={Size.LG}
        className='ad-ops-elem'
        loading={activeAPICall}
        onClick={addFlashtalkingCreativeLibrary}
        data-testid='ft-creative-library-add'
      >
        Add
      </Button>
    </Card>
  );
};
